import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckDouble, faGear } from '@fortawesome/pro-solid-svg-icons'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { CardHeader } from '../../../../shared/card/components/card-header'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { LoadingState } from '../../../../shared/empty-state/empty-state'
import { useMinuteClock } from '../../../../shared/date/use-minute-clock'
import { archive, isOffline, listInbox, markAllRead, markRead } from '../../../../api-new/state/notify'
import { useSelfNotifyAccess } from '../../../../shared/notify/self-access'
import { setUnread, useUnread } from './unread-store'
import { groupByDay } from './inbox-groups'
import { InboxRow, PREFERENCES_PATH, linkHref } from './inbox-row'
import { InboxEmpty } from './inbox-empty'
import './notifications.scss'

export { PREFERENCES_PATH, linkHref }

export const PAGE_SIZE = 50

const FILTERS = [
  { key: 'all', label: 'All', heading: 'Inbox' },
  { key: 'unread', label: 'Unread', heading: 'Unread' },
  { key: 'archived', label: 'Archived', heading: 'Archived' }
]

const OFFLINE = "We can't reach notifications right now. Check your connection, then try again."
const LOAD_FAILED = "Your notifications couldn't be loaded."
const ACTION_FAILED = "That couldn't be saved. Please try again."

// The person's inbox in the active organization (C-4 /inbox): newest first,
// paged by cursor ("Show older"), with All / Unread / Archived, mark read,
// mark all as read and archive. It reads once and never polls by itself:
// the header bell polls the unread count (edxr-bg=1), and this page
// refetches its first page, as a background request, only when the bell
// sees a newer item (latestId moved).
export const InboxPage = () => {
  const access = useSelfNotifyAccess()
  const unread = useUnread()
  const [filter, setFilter] = useState('all')
  const [items, setItems] = useState([])
  const [cursor, setCursor] = useState(null)
  const [state, setState] = useState('loading') // loading | ready | offline | error
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const [actionError, actionFailure, setActionError] = useErrorLine()
  const [busy, setBusy] = useState(null)
  const live = useRef(true)
  const filterRef = useRef(filter)
  filterRef.current = filter
  const itemsRef = useRef(items)
  itemsRef.current = items

  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const loadFirst = useCallback(async ({ background = false } = {}) => {
    const asked = filterRef.current
    if (!background) setState(s => (s === 'ready' ? s : 'loading'))
    try {
      const page = await listInbox({ filter: asked, limit: PAGE_SIZE, cursor: undefined }, { background })
      if (!live.current || filterRef.current !== asked) return
      setItems((page && page.items) || [])
      setCursor((page && page.cursor) || null)
      if (page && typeof page.unreadCount === 'number') setUnread({ count: page.unreadCount })
      setError(null)
      setState('ready')
    } catch (err) {
      if (!live.current || filterRef.current !== asked) return
      // A background refresh that fails keeps what is on screen.
      if (background) return
      if (isOffline(err)) {
        setError(null)
        setState('offline')
      } else {
        setError(serverMessage(err, LOAD_FAILED), err)
        setState('error')
      }
    }
  }, [setError])

  // A new tab starts loading (never the old tab's rows, nor the new tab's
  // empty state before its answer).
  useEffect(() => {
    setItems([])
    setCursor(null)
    setState('loading')
    loadFirst()
  }, [filter, loadFirst])

  // The rows on screen all went (archived, or read in Unread) while the
  // server has more: ask again rather than say the tab is empty.
  useEffect(() => {
    if (state === 'ready' && items.length === 0 && cursor && !loadingMore) {
      setCursor(null)
      setState('loading')
      loadFirst()
    }
  }, [state, items.length, cursor, loadingMore, loadFirst])

  // Something new arrived (the bell's poll moved latestId): refetch the
  // first page quietly. The first value seen is the baseline.
  const seenLatest = useRef(undefined)
  useEffect(() => {
    if (seenLatest.current === undefined) {
      seenLatest.current = unread.latestId
      return
    }
    if (unread.latestId === seenLatest.current) return
    seenLatest.current = unread.latestId
    // Already on screen (the first count after the page loaded, or an
    // action's answer): nothing to fetch.
    if (!unread.latestId || itemsRef.current.some(i => i.id === unread.latestId)) return
    if (filterRef.current !== 'archived') loadFirst({ background: true })
  }, [unread.latestId, loadFirst])

  const loadMore = async () => {
    if (!cursor || loadingMore) return
    const asked = filter
    setLoadingMore(true)
    try {
      const page = await listInbox({ filter: asked, limit: PAGE_SIZE, cursor }, { background: false })
      if (!live.current || filterRef.current !== asked) return
      const seen = new Set(items.map(i => i.id))
      setItems(list => [...list, ...((page && page.items) || []).filter(i => !seen.has(i.id))])
      setCursor((page && page.cursor) || null)
      setActionError(null)
    } catch (err) {
      if (live.current) setActionError(isOffline(err) ? OFFLINE : serverMessage(err, "Older notifications couldn't be loaded."), isOffline(err) ? null : err)
    } finally {
      if (live.current) setLoadingMore(false)
    }
  }

  const act = async (key, call, apply) => {
    if (!access.canWrite || busy) return
    setBusy(key)
    setActionError(null)
    try {
      const next = await call()
      // The bell's count is a module store: it follows even when the page
      // has gone (a row click marks read and navigates in the same tick).
      if (next) setUnread(next)
      if (!live.current) return
      apply()
    } catch (err) {
      if (live.current) setActionError(isOffline(err) ? OFFLINE : serverMessage(err, ACTION_FAILED), isOffline(err) ? null : err)
    } finally {
      if (live.current) setBusy(null)
    }
  }

  const now = () => new Date().toISOString()

  // Where focus goes after a row action takes away what had it (the row, in
  // Unread or on Archive; its Mark as read button, in All): the same row if
  // it is still there, else the next row (or the one before), else the
  // panel. Only when focus was actually lost.
  const panelRef = useRef(null)
  const refocus = useRef(null)
  const rememberFocus = item => {
    const list = itemsRef.current
    const i = list.findIndex(x => x.id === item.id)
    const neighbour = list[i + 1] || list[i - 1]
    refocus.current = { id: item.id, next: neighbour ? neighbour.id : null }
  }
  useEffect(() => {
    const want = refocus.current
    if (!want || !panelRef.current) return
    refocus.current = null
    const active = document.activeElement
    if (active && active !== document.body && document.body.contains(active)) return
    const rows = Array.from(panelRef.current.querySelectorAll('[data-row-id]'))
    const row = rows.find(r => r.getAttribute('data-row-id') === String(want.id)) || rows.find(r => r.getAttribute('data-row-id') === String(want.next))
    const target = row ? row.querySelector('a.nt-title, .nt-more') : panelRef.current
    if (target) target.focus()
  }, [items])

  const onRead = item =>
    act(`read:${item.id}`, () => markRead(item.id), () => {
      rememberFocus(item)
      setItems(list => (filter === 'unread' ? list.filter(i => i.id !== item.id) : list.map(i => (i.id === item.id ? { ...i, read: true, readAt: i.readAt || now() } : i))))
    })

  const onArchive = item =>
    act(`archive:${item.id}`, () => archive(item.id), () => {
      rememberFocus(item)
      setItems(list => list.filter(i => i.id !== item.id))
    })

  const onReadAll = () =>
    act('read-all', async () => (await markAllRead()) || { count: 0 }, () =>
      setItems(list => (filter === 'unread' ? [] : list.map(i => (i.read ? i : { ...i, read: true, readAt: now() })))))

  const count = unread.known ? unread.count : items.filter(i => !i.read).length

  // The page's one clock for "5 minutes ago" and the day groups.
  const clock = useMinuteClock()
  const groups = useMemo(() => groupByDay(items, { now: clock }), [items, clock])
  const current = FILTERS.find(f => f.key === filter) || FILTERS[0]

  // The tabs: a roving tablist (arrows move and choose, Home / End).
  const tabRefs = useRef({})
  const onTabKey = e => {
    const i = FILTERS.findIndex(f => f.key === filter)
    const to = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: FILTERS.length - 1 }[e.key]
    if (to === undefined) return
    e.preventDefault()
    const next = FILTERS[(to + FILTERS.length) % FILTERS.length]
    setFilter(next.key)
    const el = tabRefs.current[next.key]
    if (el) el.focus()
  }

  const headerActions =
    <>
      {count > 0 && filter !== 'archived' && <span className="sw-hint nt-count">{`${count} unread`}</span>}
      {filter !== 'archived' &&
        <button type="button" className="btn btn-sm sw-btn" disabled={!access.canWrite || count === 0 || busy === 'read-all'} onClick={onReadAll}>
          <FontAwesomeIcon icon={faCheckDouble} className="me-1" />Mark all as read
        </button>}
    </>

  return (
    <FleetPage
      title="Notifications"
      purpose="What EducationXR told you in this organization, newest first."
      testId="notify-inbox"
      actions={
        <Link className="btn btn-sm sw-btn" to={PREFERENCES_PATH}>
          <FontAwesomeIcon icon={faGear} className="me-1" />Preferences
        </Link>
      }>
      {access.reason && <div className="mb-3"><Notice tone="warn" role="status">{access.reason}</Notice></div>}

      <div className="sw-toolbar nt-toolbar">
        <div className="nt-tabs" role="tablist" aria-label="Show">
          {FILTERS.map(f =>
            <button key={f.key} type="button" role="tab" id={`nt-tab-${f.key}`}
              ref={el => {
                tabRefs.current[f.key] = el
              }}
              aria-selected={filter === f.key ? 'true' : 'false'} aria-controls="nt-panel" tabIndex={filter === f.key ? 0 : -1}
              className={`nt-tab${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)} onKeyDown={onTabKey}>
              {f.label}
              {f.key === 'unread' && count > 0 && <span className="nt-tab-count">{count > 99 ? '99+' : count}</span>}
            </button>)}
        </div>
      </div>

      <div className="card nt-card">
        <div className="card-body">
          <CardHeader title={current.heading} actions={headerActions} />

          <div id="nt-panel" role="tabpanel" aria-labelledby={`nt-tab-${filter}`} className="nt-panel" tabIndex={-1} ref={panelRef}>
            {actionError && <p className="text-danger small mt-2" role="alert">{actionError}</p>}
            {actionError && <ErrorReference error={actionFailure} className="mb-2" />}

            {state === 'loading' && <LoadingState rows={4} label="Loading notifications" />}

            {state === 'offline' &&
              <div className="nt-state">
                <Notice tone="warn" role="alert">
                  <p className="mb-2">{OFFLINE}</p>
                  <button type="button" className="btn btn-sm sw-btn" onClick={() => loadFirst()}>Try again</button>
                </Notice>
              </div>}

            {state === 'error' &&
              <div className="nt-state">
                <p className="text-danger small" role="alert">{error}</p>
                <ErrorReference error={failure} className="mb-2" />
                <button type="button" className="btn btn-sm sw-btn" onClick={() => loadFirst()}>Try again</button>
              </div>}

            {state === 'ready' && items.length === 0 && <InboxEmpty filter={filter} />}

            {state === 'ready' && items.length > 0 &&
              <div className="nt-groups" data-testid="inbox-groups">
                {groups.map(group =>
                  <div key={group.key} className="nt-group" data-testid={`inbox-group-${group.key}`}>
                    <h5 className="nt-group-title" id={`nt-group-${group.key}`}>{group.label}</h5>
                    <ul className="nt-list" aria-labelledby={`nt-group-${group.key}`}>
                      {group.items.map(item =>
                        <InboxRow key={item.id} item={item} now={clock} canWrite={access.canWrite} readOnlyReason={access.reason} busy={busy}
                          onRead={() => onRead(item)} onArchive={() => onArchive(item)} />)}
                    </ul>
                  </div>)}
                {cursor &&
                  <div className="nt-more-row">
                    <button type="button" className="btn btn-sm sw-btn" disabled={loadingMore} onClick={loadMore}>
                      {loadingMore ? 'Loading…' : 'Show older'}
                    </button>
                  </div>}
              </div>}
          </div>
        </div>
      </div>
    </FleetPage>
  )
}
